import { useCallback, useEffect, useState } from "react";
import { messageFor } from "@/lib/errors";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { listBondDocuments, type BondDocument } from "@/lib/brokerBonds";

// BRK-S146 — one bond order's documents, read ONCE per page.
//
// ── WHY THE PAGE OWNS THIS AND NOT THE SECTION ───────────────────────────────
//
// Two surfaces on the Submission Profile care about the same documents: the
// Documents section lists them all, and the bid-result panel needs to know
// whether the signed contract is on file. The panel used to answer that from
// `order.finalContract` — a pointer on the order view — which made TWO sources
// for one fact: the order could say a contract exists while the list did not
// show it, and the filename was rendered twice on one page.
//
// So the read lives at the page and both children render from it. `reload` is
// what the panel calls after an upload, because a document it just stored has
// to appear in the list the section renders — refreshing the ORDER alone would
// leave the section a document behind.

export interface BondDocumentsState {
  /** null while loading, and after a failed read. */
  documents: BondDocument[] | null;
  /** PMS's own sentence when the read failed, else null. */
  error: string | null;
  /** Re-read — after an upload or a removal. */
  reload: () => void;
}

export function useBondDocuments(
  request: AuthedRequester,
  submissionId: string | undefined,
): BondDocumentsState {
  const [documents, setDocuments] = useState<BondDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(0);

  useEffect(() => {
    if (submissionId === undefined) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await listBondDocuments(request, submissionId);
        if (cancelled) return;
        setDocuments(body.documents);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        // A handled state, never a throw into the render (NN #7). A failed
        // sub-read must not take down the record around it.
        setDocuments(null);
        setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request, submissionId, token]);

  const reload = useCallback(() => {
    setToken((n) => n + 1);
  }, []);

  return { documents, error, reload };
}

/**
 * The order's signed contract, or null — the NEWEST one, because a re-upload
 * appends a new version rather than replacing (PMS-S304).
 *
 * Read off the documents LIST rather than off the order's `finalContract`
 * pointer, so the bid-result panel and the Documents section cannot disagree
 * about whether a contract is on file.
 */
export function signedContractOf(
  documents: readonly BondDocument[] | null,
): BondDocument | null {
  if (!documents) return null;
  const contracts = documents.filter((d) => d.type === "final_contract");
  if (contracts.length === 0) return null;
  return contracts.reduce((newest, next) =>
    next.uploadedAt > newest.uploadedAt ? next : newest,
  );
}
